import React, { useState } from "react";
import {
  useDeleteOrgMutation,
  useGetOrgsQuery,
  useUpdateOrgsMutation,
} from "../../store/auth/orgapi";

import { useGetOrgTypesQuery } from "../../store/auth/orgtypeapi";

import {
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Typography,
  message,
} from "antd";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";

function Org() {
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const columns = [
    {
      key: "_id",
      title: "ID",
      dataIndex: "_id",
      render: (id) => (
        <Typography.Text copyable={{ text: id }}>{id}</Typography.Text>
      ),
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      render: (types) => {
        return types?.map((type) => type.name).join(", ");
      },
    },
    {
      key: "address",
      title: "Address",
      dataIndex: "address",
    },
    {
      key: "baddress",
      title: "Blockchain Address",
      dataIndex: "baddress",
      render: (baddress) => {
        if (!baddress) return <span style={{ color: "#999" }}>—</span>;
        return (
          <Typography.Text copyable={{ text: baddress }} style={{ fontFamily: "monospace" }}>
            {`${baddress.slice(0, 8)}…${baddress.slice(-6)}`}
          </Typography.Text>
        );
      },
    },
    {
      key: "createdAt",
      title: "Created",
      dataIndex: "createdAt",
      render: (record) => {
        return (
          <span>
            {record
              ? moment
                  .utc(record)
                  .local()
                  .format("DD MMM, YYYY @ hh:mm:ss")
              : undefined}
          </span>
        );
      },
    },
    {
      key: "updatedAt",
      title: "Updated",
      dataIndex: "updatedAt",
      render: (record) => {
        return (
          <span>
            {record
              ? moment
                  .utc(record)
                  .local()
                  .format("DD MMM, YYYY @ hh:mm:ss")
              : undefined}
          </span>
        );
      },
    },
    {
      key: "6",
      title: "Actions",
      render: (record) => {
        return (
          <>
            <EditOutlined
              data-testid="edit-icon"
              onClick={() => {
                onEditRecord(record);
              }}
              style={{ marginRight: 10 }}
            />
            <Divider type="vertical"></Divider>
            <DeleteOutlined
              data-testid="delete-icon"
              onClick={() => {
                onDeleteRecord(record);
              }}
              style={{ color: "red", marginLeft: 10 }}
            />
          </>
        );
      },
    },
  ];

  const { data = {}, isLoading } = useGetOrgsQuery();
  const [updateOrgs] = useUpdateOrgsMutation();
  const [deleteOrg] = useDeleteOrgMutation();

  const key = "updatable";
  const onEditRecord = (record) => {
    setEditingRecord({ ...record });
    setIsEditing(true);
  };

  const resetEditing = () => {
    setEditingRecord(null);
    setIsEditing(false);
  };

  const handleEditRecord = async (record) => {
    try {
      await updateOrgs(record)
        .unwrap()
        .then(() => {
          message.success({
            content: "Organization updated successfully!",
            key,
            duration: 2,
          });
        })
        .catch((err) => {
          message.error({
            content: "Failed to update Organization!",
            key,
            duration: 2,
          });
          console.log(err);
        });
    } catch {}
  };

  const handleDeleteRecord = async (id) => {
    try {
      await deleteOrg(id)
        .unwrap()
        .then(() => {
          message.success({
            content: "Organization deleted successfully!",
            key,
            duration: 2,
          });
        })
        .catch((err) => {
          message.error({
            content: "Failed to delete Organization!",
            key,
            duration: 2,
          });
          console.log(err);
        });
    } catch {}
  };

  const onCreate = (values) => {
    editingRecord.name = values.name;
    editingRecord.type = values.type;
    editingRecord.address = values.address;
    handleEditRecord(editingRecord);
    setIsEditing(false);
  };

  const onDeleteRecord = (record) => {
    Modal.confirm({
      title: "Are you sure, you want to delete this record?",
      okText: "Yes",
      okType: "danger",
      className: "delete-confirm-modal",
      onOk: () => {
        handleDeleteRecord({ ...record }._id);
      },
      okButtonProps: {
        'data-testid': 'delete-confirm-yes'
      }
    });
  };

  return (
    <div>
      <Typography.Title level={2}>Organizations</Typography.Title>
      <div>
        <Card className="shadow-card" style={{ borderRadius: 8 }}>
          <Table
            size="small"
            columns={columns}
            dataSource={data.orgList}
            loading={isLoading}
          ></Table>
        </Card>
      </div>
      {isEditing ? (
        <OrganizationFormModal
          visible={isEditing}
          onCreate={onCreate}
          onCancel={() => {
            resetEditing();
          }}
          editingRecord={editingRecord}
        />
      ) : null}
    </div>
  );
}

const OrganizationFormModal = ({
  visible,
  onCreate,
  onCancel,
  editingRecord,
}) => {
  const {
    data: orgtypes,
    isLoading,
  } = useGetOrgTypesQuery();

  const { Option } = Select;

  const orgTypes = [];
  if (!isLoading && Array.isArray(orgtypes)) {
    orgtypes.forEach((orgtype) => {
      orgTypes.push(<Option key={orgtype._id}>{orgtype.name}</Option>);
    });
  }
  const initialTypeValues = editingRecord?.type?.map((t) => t._id) || [];

  const [form] = Form.useForm();
  return (
    <Modal
      width={500}
      title="Edit Organization"
      open={visible}
      okText="Save"
      className="org-form-modal"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            form.resetFields();
            onCreate(values);
          })
          .catch((info) => {
            console.log("Validate Failed:", info);
          });
      }}
      okButtonProps={{
        'data-testid': 'org-form-save'
      }}
    >
      <Form layout="vertical" form={form} name="organization">
        <Form.Item
          name="name"
          label="Name"
          initialValue={editingRecord?.name}
          rules={[
            {
              required: true,
              message: "Please input Organization name!",
            },
          ]}
        >
          <Input data-testid="org-name-input" />
        </Form.Item>
        <Form.Item
          initialValue={initialTypeValues}
          name="type"
          label="Type"
          rules={[]}
        >
          <Select
            data-testid="org-type-select"
            style={{
              width: "100%",
            }}
            placeholder="Please select the type"
            mode="multiple"
          >
            {orgTypes}
          </Select>
        </Form.Item>
        <Form.Item
          name="address"
          label="Address"
          initialValue={editingRecord?.address}
          rules={[
            {
              required: true,
              message: "Please input Address!",
            },
          ]}
        >
          <TextArea data-testid="org-address-input" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Org;
